import { fetchWithAuth } from '../utils/fetchWithAuth';

class AIActionManager {
  constructor() {
    this.actionParsers = new Map();
    this.actionExecutors = new Map();
    this.actionValidators = new Map();
    this.actionPreviewers = new Map();

    // Initialize built-in parsers and executors
    this.initializeBuiltInCapabilities();
  }

  // Register a new AI capability
  registerCapability(capabilityId, config) {
    const { parser, executor, validator, previewer, description } = config;

    if (parser) this.actionParsers.set(capabilityId, parser);
    if (executor) this.actionExecutors.set(capabilityId, executor);
    if (validator) this.actionValidators.set(capabilityId, validator);
    if (previewer) this.actionPreviewers.set(capabilityId, previewer);

    console.log(`🔧 Registered AI capability: ${capabilityId}`);
  }

  // Parse AI response for any actionable capabilities
  async parseAIResponse(aiResponse, context = {}) {
    const detectedActions = [];

    // First, check if bulk_actions parser detects a bulk action
    const bulkParser = this.actionParsers.get('bulk_actions');
    if (bulkParser) {
      try {
        const bulkActions = await bulkParser(aiResponse, context);
        if (bulkActions && bulkActions.length > 0) {
          // If bulk actions are detected, return only those and skip individual parsers
          console.log('🔍 Bulk actions detected, skipping individual parsers to prevent duplicates');
          bulkActions.forEach(action => {
            detectedActions.push({
              capabilityId: 'bulk_actions',
              ...action,
              originalResponse: aiResponse
            });
          });
          console.log(`🔍 ✅ Returning ${detectedActions.length} bulk actions only - no individual parsers will run`);
          return detectedActions;
        }
      } catch (error) {
        console.warn(`Bulk parser error:`, error);
      }
    }

    console.log('🔍 No bulk actions detected, running individual parsers');
    
    // Only run individual parsers if no bulk actions were detected
    for (const [capabilityId, parser] of this.actionParsers.entries()) {
      // Skip bulk_actions parser since we already tried it
      if (capabilityId === 'bulk_actions') continue;

      try {
        const actions = await parser(aiResponse, context);
        if (actions && actions.length > 0) {
          console.log(`🔍 Individual parser ${capabilityId} found ${actions.length} actions`);
          actions.forEach(action => {
            detectedActions.push({
              capabilityId,
              ...action,
              originalResponse: aiResponse
            });
          });
        }
      } catch (error) {
        console.warn(`Parser error for ${capabilityId}:`, error);
      }
    }

    // Centralized deduplication: Remove duplicate actions across all capabilities
    const deduplicatedActions = this.deduplicateActions(detectedActions);
    
    console.log(`🔍 Final parsing result: ${deduplicatedActions.length} unique actions detected (${detectedActions.length - deduplicatedActions.length} duplicates removed)`);
    return deduplicatedActions;
  }
  
  // Centralized deduplication using stable hash of capabilityId + data
  deduplicateActions(actions) {
    const seen = new Set();
    const uniqueActions = [];
    
    for (const action of actions) {
      // Create stable hash from capabilityId and data
      const stableKey = this.createActionHash(action);
      
      if (seen.has(stableKey)) {
        console.log(`🔍 Deduplicating action: ${action.capabilityId}`);
        continue;
      }
      
      seen.add(stableKey);
      uniqueActions.push(action);
    }
    
    return uniqueActions;
  }
  
  // Create a stable hash for action deduplication
  createActionHash(action) {
    try {
      // Use capabilityId + sorted JSON of data for consistent hashing
      const dataStr = action.data ? JSON.stringify(action.data, Object.keys(action.data).sort()) : '';
      return `${action.capabilityId}:${dataStr}`;
    } catch (e) {
      // Fallback to simpler hash if JSON.stringify fails
      return `${action.capabilityId}:${Date.now()}`;
    }
  }

  // Validate if an action can be executed
  async validateAction(action, context = {}) {
    const validator = this.actionValidators.get(action.capabilityId);
    if (!validator) return true; // No validator means always valid

    try {
      return await validator(action, context);
    } catch (error) {
      console.error(`Validation error for ${action.capabilityId}:`, error);
      return false;
    }
  }

  // Generate action preview for UI
  async generatePreview(action, context = {}) {
    const previewer = this.actionPreviewers.get(action.capabilityId);
    if (!previewer) {
      return {
        type: 'generic',
        title: action.capabilityId.replace(/_/g, ' '),
        description: action.description || 'AI wants to perform an action'
      };
    }

    try {
      return await previewer(action, context);
    } catch (error) {
      console.error(`Preview error for ${action.capabilityId}:`, error);
      return null;
    }
  }

  // Execute an action
  async executeAction(action, context = {}) {
    const executor = this.actionExecutors.get(action.capabilityId);
    if (!executor) {
      throw new Error(`No executor found for capability: ${action.capabilityId}`);
    }

    // Validate before execution
    const isValid = await this.validateAction(action, context);
    if (!isValid) {
      throw new Error(`Action validation failed for: ${action.capabilityId}`);
    }

    try {
      console.log(`🤖 Executing AI action: ${action.capabilityId}`, action.data);
      const result = await executor(action, context);
      console.log(`✅ AI action completed: ${action.capabilityId}`);
      return result;
    } catch (error) {
      console.error(`❌ AI action failed: ${action.capabilityId}`, error);
      throw error;
    }
  }

  // Initialize built-in capabilities
  initializeBuiltInCapabilities() {
    // Tool Duration Editing Capability
    this.registerCapability('tool_duration_editing', {
      parser: this.parseToolDurationAction.bind(this),
      executor: this.executeToolDurationAction.bind(this),
      validator: this.validateToolDurationAction.bind(this),
      previewer: this.previewToolDurationAction.bind(this)
    });

    // Universal Bulk Actions Capability
    this.registerCapability('bulk_actions', {
      parser: this.parseBulkActionsAction.bind(this),
      executor: this.executeBulkActionsAction.bind(this),
      validator: this.validateBulkActionsAction.bind(this),
      previewer: this.previewBulkActionsAction.bind(this)
    });

    // Tool Management Capabilities
    this.registerCapability('add_tools_to_stages', {
      parser: this.parseAddToolsAction.bind(this),
      executor: this.executeAddToolsAction.bind(this),
      validator: this.validateAddToolsAction.bind(this),
      previewer: this.previewAddToolsAction.bind(this)
    });

    this.registerCapability('remove_tools_from_stages', {
      parser: this.parseRemoveToolsAction.bind(this),
      executor: this.executeRemoveToolsAction.bind(this),
      validator: this.validateRemoveToolsAction.bind(this),
      previewer: this.previewRemoveToolsAction.bind(this)
    });

    this.registerCapability('modify_tools_content', {
      parser: this.parseModifyToolsContentAction.bind(this),
      executor: this.executeModifyToolsContentAction.bind(this),
      validator: this.validateModifyToolsContentAction.bind(this),
      previewer: this.previewModifyToolsContentAction.bind(this)
    });

    // FIX #15: Add save_plan capability with proper parser (like other capabilities)
    this.registerCapability('save_plan', {
      parser: this.parseSavePlanAction.bind(this),
      executor: this.executeSavePlanAction.bind(this),
      validator: this.validateSavePlanAction.bind(this),
      previewer: this.previewSavePlanAction.bind(this)
    });

    // Strategy Component Updates - Generic handler
    this.registerCapability('update_strategy_component', {
      parser: this.parseStrategyUpdateAction.bind(this),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    // Strategy Component Updates - Specific handlers with focused parsers
    this.registerCapability('update_goal_statement', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_goal_statement'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_problem_statement', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_problem_statement'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_target_statement', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_target_statement'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_design_challenge', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_design_challenge'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_objectives', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_objectives'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_deliverables', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_deliverables'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_metrics', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_metrics'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_stakeholders', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_stakeholders'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_summary', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_summary'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_experience_design_principles', {
      parser: (aiResponse, context) => this.parseSpecificStrategyUpdate(aiResponse, context, 'update_experience_design_principles'),
      executor: this.executeStrategyUpdateAction.bind(this),
      validator: this.validateStrategyUpdateAction.bind(this),
      previewer: this.previewStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_strategic_themes', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_strategic_themes'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_design_vision', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_design_vision'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_audience_insights', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_audience_insights'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_opportunity_areas', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_opportunity_areas'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_design_hypotheses', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_design_hypotheses'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_brief_deconstruction', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_brief_deconstruction'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_information_assessment', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_information_assessment'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_strategic_guidance', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_strategic_guidance'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_competitive_ux_positioning', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_competitive_ux_positioning'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_success_metrics', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_success_metrics'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });

    this.registerCapability('update_unmet_needs', {
      parser: (aiResponse, context) => this.parseProductStrategyUpdate(aiResponse, context, 'update_unmet_needs'),
      executor: this.executeProductStrategyUpdateAction.bind(this),
      validator: this.validateProductStrategyUpdateAction.bind(this),
      previewer: this.previewProductStrategyUpdateAction.bind(this)
    });
  }

  // Universal Bulk Actions Parsing - ENHANCED APPROACH
  async parseBulkActionsAction(aiResponse, context) {
    const actions = [];
    console.log('🔍 BULK ACTIONS PARSER: Starting analysis of AI response');
    console.log('🔍 AI Response length:', aiResponse.length);

    // Look for multiple actions in single JSON or multiple JSON blocks
    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];
    const allDetectedActions = [];

    console.log('🔍 Found JSON blocks:', jsonMatches.length);

    // Collect all individual actions first
    for (const [matchIndex, match] of jsonMatches.entries()) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        console.log(`🔍 Parsing JSON block ${matchIndex + 1}:`, jsonStr.substring(0, 200) + '...');

        const parsed = JSON.parse(jsonStr);
        console.log(`🔍 Parsed structure for block ${matchIndex + 1}:`, {
          hasCapability: !!parsed.capability,
          hasAction: !!parsed.action,
          capabilityValue: parsed.capability,
          actionValue: parsed.action,
          hasData: !!parsed.data,
          hasActions: !!parsed.actions,
          dataStructure: parsed.data ? (Array.isArray(parsed.data) ? `Array[${parsed.data.length}]` : Object.keys(parsed.data)) : null,
          actionsStructure: parsed.actions ? (Array.isArray(parsed.actions) ? `Array[${parsed.actions.length}]` : typeof parsed.actions) : null
        });

        // Handle legacy format conversion: "action" -> "capability"
        if (parsed.action && !parsed.capability) {
          console.log('🔄 Converting legacy action format to capability format:', parsed.action);
          parsed.capability = parsed.action;
          // Move all fields except 'action' into 'data' if not already structured
          if (!parsed.data) {
            const { action, ...rest } = parsed;
            parsed.data = rest;
          }
          delete parsed.action;
        }

        // Handle "modifications" array format -> convert to bulk_actions
        if (parsed.modifications && Array.isArray(parsed.modifications) && !parsed.capability) {
          console.log('🔄 Converting modifications array to bulk_actions format');
          const bulkActions = parsed.modifications.map(mod => ({
            type: 'tool_duration_editing',
            data: {
              programIndex: this.findProgramIndex(mod.program, context),
              stage: mod.stage,
              toolName: mod.tool,
              newDuration: mod.new_duration || mod.newDuration
            }
          }));

          parsed.capability = 'bulk_actions';
          parsed.data = {
            programIndex: 0,
            stage: 'mixed',
            isCrossProgram: true,
            actions: bulkActions
          };
          delete parsed.modifications;
        }

        // Check if this is a bulk_actions format - HANDLE BOTH STRUCTURES
        if (parsed.capability === 'bulk_actions') {
          console.log('🔍 BULK_ACTIONS detected - checking data structure');

          let bulkActions = null;
          let baseData = {};

          // Handle both data structures:
          // 1. { "capability": "bulk_actions", "data": { "actions": [...] } }
          // 2. { "capability": "bulk_actions", "data": [...] }
          if (parsed.data?.actions && Array.isArray(parsed.data.actions)) {
            // Structure 1: actions nested under data.actions
            bulkActions = parsed.data.actions;
            baseData = { ...parsed.data };
            delete baseData.actions; // Remove actions from baseData to avoid duplication
            console.log('🔍 Using nested actions structure (data.actions)');
          } else if (Array.isArray(parsed.data)) {
            // Structure 2: actions directly in data array
            bulkActions = parsed.data;
            baseData = {};
            console.log('🔍 Using direct array structure (data as array)');
          } else {
            console.warn('🔍 ❌ Invalid bulk_actions structure:', parsed.data);
            continue;
          }

          console.log('🔍 Extracted bulk actions:', {
            bulkActionsType: `Array[${bulkActions.length}]`,
            bulkActionsPreview: bulkActions.map(a => ({
              type: a.action || a.capability || a.type || a.operation,
              hasData: !!a.data,
              dataKeys: a.data ? Object.keys(a.data) : Object.keys(a).filter(k => k !== 'action' && k !== 'capability' && k !== 'type' && k !== 'operation'),
              programIndex: a.programIndex || a.data?.programIndex,
              stage: a.stage || a.data?.stage
            })),
            baseData
          });

          // Process each action in the bulk request with better normalization
          const processedActions = bulkActions.map((action, index) => {
            console.log(`🔍 Processing action ${index + 1}:`, action);

            // Determine action type from various possible fields
            let actionType = action.type || action.capability || action.action || action.operation;

            // If no explicit type, try to infer from data structure
            if (!actionType) {
              if (action.toolName || action.tool || action.newDuration || action.duration) {
                actionType = 'tool_duration_editing';
              } else if (action.tools && Array.isArray(action.tools)) {
                actionType = 'add_tools_to_stages';
              }
            }

            // Create normalized action data
            let actionData = {};

            // If action has a nested data object, use it as base
            if (action.data && typeof action.data === 'object') {
              actionData = { ...action.data };
            } else {
              // Otherwise, use the action object itself (excluding type fields)
              actionData = { ...action };
              // Remove type-related fields from data
              delete actionData.type;
              delete actionData.capability;
              delete actionData.action;
              delete actionData.operation;
            }

            // Normalize tool fields for remove actions
            if (actionType === 'remove_tools_from_stages') {
              if (actionData.toolName && !actionData.tools) {
                actionData.tools = [actionData.toolName];
              }
              if (actionData.tool && !actionData.tools) {
                actionData.tools = [actionData.tool];
              }
            }

            // Ensure required fields are present with fallbacks
            const normalizedData = {
              programIndex: actionData.programIndex ?? baseData.programIndex ?? 1,
              stage: actionData.stage ?? baseData.stage ?? 'Define',
              toolName: actionData.toolName || actionData.tool,
              newDuration: actionData.newDuration || actionData.duration,
              tools: actionData.tools,
              ...actionData // Include any other fields
            };

            console.log(`🔍 Normalized action ${index + 1}:`, {
              type: actionType,
              data: normalizedData
            });

            return {
              type: actionType,
              data: normalizedData,
              description: action.description || `${actionType} operation`
            };
          });

          // Enhance the bulk action data to support cross-program and cross-stage operations
          const scopeAnalysis = this.detectCrossProgramActions(processedActions, context);
          const enhancedData = {
            programIndex: baseData.programIndex ?? processedActions[0]?.data.programIndex ?? 1,
            stage: baseData.stage ?? processedActions[0]?.data.stage ?? 'Define',
            isCrossProgram: scopeAnalysis.isCrossProgram,
            isCrossStage: scopeAnalysis.isCrossStage,
            actions: processedActions
          };

          console.log('🔍 Enhanced bulk action data:', enhancedData);

          actions.push({
            data: enhancedData,
            description: parsed.description || `Bulk operation with ${bulkActions.length} actions`
          });
        }
        // Check if this is a multi-action scenario - STANDARDIZED to use 'capability' only
        else if (parsed.capability) {
          console.log('🔍 Individual capability detected:', {
            capability: parsed.capability,
            hasData: !!parsed.data,
            dataKeys: parsed.data ? Object.keys(parsed.data) : null
          });

          // Use standardized capability format
          const standardizedAction = {
            capability: parsed.capability,
            data: parsed.data || {},
            description: parsed.description
          };
          allDetectedActions.push(standardizedAction);

          // For individual actions, don't add to actions array here
          // Let the logic below handle single vs multiple actions properly
        } else {
          console.log('🔍 ❌ Unrecognized JSON structure:', {
            keys: Object.keys(parsed),
            structure: parsed
          });
        }
      } catch (e) {
        console.warn('🔍 JSON parsing error:', e.message);
        continue;
      }
    }

    // Deduplicate allDetectedActions — the LLM sometimes outputs the same JSON block twice.
    // If all detected actions are identical (same capability + same data), collapse to one.
    if (allDetectedActions.length > 1) {
      const seen = new Set();
      const deduped = allDetectedActions.filter(a => {
        const key = `${a.capability}::${JSON.stringify(a.data)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (deduped.length < allDetectedActions.length) {
        console.log(`🔍 Deduplicated ${allDetectedActions.length} → ${deduped.length} actions (duplicate JSON blocks removed)`);
        allDetectedActions.length = 0;
        deduped.forEach(a => allDetectedActions.push(a));
      }
    }

    // Create bulk actions for multiple actions OR mixed action types
    if (allDetectedActions.length > 1 && actions.length === 0) {
      console.log(`🔍 Multiple individual actions detected (${allDetectedActions.length}), creating bulk action`);
      
      // Detect if actions span multiple programs/stages
      const programStageMap = new Map();
      allDetectedActions.forEach(action => {
        const key = `${action.data?.programIndex || 0}-${action.data?.stage || 'unknown'}`;
        if (!programStageMap.has(key)) {
          programStageMap.set(key, []);
        }
        programStageMap.get(key).push(action);
      });

      const isCrossProgram = programStageMap.size > 1;
      const isCrossStage = !isCrossProgram && new Set(allDetectedActions.map(a => a.data?.stage)).size > 1;

      // Normalize data for tool removal actions - convert toolName to tools array
      const normalizedActions = allDetectedActions.map(action => {
        const normalizedData = { ...action.data };

        // Fix tool removal data structure
        if (action.capability === 'remove_tools_from_stages') {
          if (normalizedData.toolName && !normalizedData.tools) {
            normalizedData.tools = [normalizedData.toolName];
            delete normalizedData.toolName; // Remove the original field to avoid confusion
          }
        }

        return {
          type: action.capability,
          data: {
            ...normalizedData,
            programIndex: action.data?.programIndex ?? (allDetectedActions[0].data?.programIndex || 0),
            stage: action.data?.stage ?? (allDetectedActions[0].data?.stage || 'unknown')
          },
          description: action.description
        };
      });

      const bulkActionData = {
        programIndex: allDetectedActions[0].data?.programIndex || 0,
        stage: allDetectedActions[0].data?.stage || 'unknown',
        isCrossProgram,
        isCrossStage,
        actions: normalizedActions
      };

      actions.push({
        data: bulkActionData,
        description: `Bulk operation: ${allDetectedActions.map(a => a.capability).join(', ')}`,
        capability: 'bulk_actions'
      });

      console.log(`🔍 ✅ Bulk action created successfully with ${normalizedActions.length} sub-actions, will prevent individual parsers from running`);
      return actions;
    }

    // Check if we have any explicit bulk_actions capability in the JSON
    if (actions.length > 0) {
      console.log('🔍 ✅ Explicit bulk_actions detected, will prevent individual parsers from running');
      return actions;
    }

    // If we only have one action detected and no bulk actions were built, return empty
    // This allows the individual parsers to handle single actions properly
    if (allDetectedActions.length === 1) {
      console.log('🔍 Single action detected, letting individual parser handle it');
      return [];
    }

    console.log('🔍 No actions detected by bulk parser');
    return [];
  }

  // Modify Tools Content Parsing
  async parseModifyToolsContentAction(aiResponse, context) {
    const actions = [];
    const seenActions = new Set(); // Track unique actions to prevent duplicates
    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];

    for (const match of jsonMatches) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        const parsed = JSON.parse(jsonStr);

        if (parsed.capability === 'modify_tools_content') {
          // Create a unique key based on the action content to detect duplicates
          const uniqueKey = JSON.stringify({
            programIndex: parsed.data?.programIndex,
            stage: parsed.data?.stage,
            toolIdentifier: parsed.data?.toolIdentifier,
            modifications: parsed.data?.modifications
          });
          
          // Skip if we've already seen this exact action
          if (seenActions.has(uniqueKey)) {
            console.log('🔍 Skipping duplicate modify_tools_content action');
            continue;
          }
          seenActions.add(uniqueKey);
          
          actions.push({
            capabilityId: 'modify_tools_content',
            data: parsed.data,
            type: 'modify_tools_content'
          });
        }
      } catch (e) {
        continue;
      }
    }

    return actions;
  }

  // Modify Tools Content Execution
  async executeModifyToolsContentAction(action, context) {
    const { handleComponentAction } = context;
    if (!handleComponentAction) {
      throw new Error('No component action handler available');
    }

    return await handleComponentAction(
      'modify_tools_content',
      'AIActionManager',
      action.data
    );
  }

  // Modify Tools Content Validation
  async validateModifyToolsContentAction(action, context) {
    const { data } = action;
    return data && 
           data.programIndex !== undefined && 
           data.stage && 
           data.toolIdentifier && 
           data.modifications &&
           Object.keys(data.modifications).length > 0;
  }

  // Modify Tools Content Preview
  async previewModifyToolsContentAction(action, context) {
    const { data } = action || {};
    const { finalProgramSequence } = context || {};

    let programName = 'Unknown Program';
    if (data?.programIndex !== undefined && Array.isArray(finalProgramSequence) && finalProgramSequence[data.programIndex]) {
      programName = finalProgramSequence[data.programIndex].program_of_work || 'Unknown Program';
    }

    const modificationsList = Object.entries(data?.modifications || {})
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');

    return {
      type: 'tool_modification',
      component: 'tool_management',
      program: programName,
      stage: data?.stage || 'Unknown Stage',
      toolIdentifier: data?.toolIdentifier || 'Unknown Tool',
      modifications: modificationsList || 'No modifications specified'
    };
  }

  // Helper method to detect cross-program and cross-stage actions
  detectCrossProgramActions(actions, context) {
    const programs = new Set();
    const stages = new Set();
    const programStages = new Set();

    actions.forEach(action => {
      const programIndex = action.data?.programIndex || 0;
      const stage = action.data?.stage || 'unknown';

      programs.add(programIndex);
      stages.add(stage);
      programStages.add(`${programIndex}-${stage}`);
    });

    const isCrossProgram = programs.size > 1;
    const isCrossStage = !isCrossProgram && stages.size > 1; // Only cross-stage if same program

    return {
      isCrossProgram,
      isCrossStage,
      isMultiLocation: programStages.size > 1 // Any multi-location operation
    };
  }

  // Helper method to find program index by name
  findProgramIndex(programName, context) {
    if (!context.finalProgramSequence || !programName) return 0;

    const index = context.finalProgramSequence.findIndex(program => 
      (program.program_of_work || program.name || '').toLowerCase() === programName.toLowerCase()
    );

    return index >= 0 ? index : 0;
  }

  // Universal Bulk Actions Execution - ENHANCED APPROACH
  async executeBulkActionsAction(action, context) {
    const { handleComponentAction } = context;
    if (!handleComponentAction) {
      throw new Error('No component action handler available');
    }

    const { actions: bulkActions, programIndex, stage, isCrossProgram } = action.data;
    const results = [];
    let successCount = 0;
    let failureCount = 0;

    const operationType = isCrossProgram ? 'cross-program' : 'single-program';
    console.log(`🔄 Executing ${bulkActions.length} bulk actions (${operationType})`);

    // Execute actions sequentially to maintain state consistency
    for (const [index, subAction] of bulkActions.entries()) {
      try {
        // Use the action's own program/stage context, not the bulk action's default
        const actionProgramIndex = subAction.data.programIndex;
        const actionStage = subAction.data.stage;

        console.log(`🔄 Executing bulk action ${index + 1}/${bulkActions.length}: ${subAction.type} on Program[${actionProgramIndex}].${actionStage}`);

        // Ensure data has the correct context and normalize field names
        const normalizedData = {
          ...subAction.data,
          programIndex: actionProgramIndex,
          stage: actionStage,
          // Normalize field names for consistency
          toolName: subAction.data.toolName || subAction.data.tool,
          newDuration: subAction.data.newDuration || subAction.data.duration
        };

        const result = await handleComponentAction(
          subAction.type,
          'AIActionManager',
          normalizedData
        );

        results.push({
          index,
          type: subAction.type,
          success: result.success,
          message: result.message,
          programIndex: actionProgramIndex,
          stage: actionStage,
          result
        });

        if (result.success) {
          successCount++;
        } else {
          failureCount++;
        }

      } catch (error) {
        console.error(`Failed to execute bulk action ${index + 1} (${subAction.type}):`, error);
        results.push({
          index,
          type: subAction.type,
          success: false,
          message: `Error: ${error.message}`,
          programIndex: subAction.data.programIndex,
          stage: subAction.data.stage,
          error: error.message
        });
        failureCount++;
      }
    }

    // FIX #3: Generate clear, actionable error messages
    let message = `Bulk operation completed: ${successCount} successful, ${failureCount} failed`;
    
    if (failureCount > 0) {
      // Extract specific error reasons from failed actions
      const failureReasons = results
        .filter(r => !r.success)
        .map(r => r.message || r.error || 'Unknown error')
        .filter((msg, idx, arr) => arr.indexOf(msg) === idx) // Deduplicate
        .slice(0, 3); // Show first 3 distinct errors
      
      if (failureReasons.length > 0) {
        message = `❌ Action failed: ${failureReasons.join(' | ')}`;
      } else {
        message = `❌ All ${failureCount} action(s) failed. Check if your plan has the required structure.`;
      }
    }

    const summary = {
      success: successCount > 0,
      message,
      totalActions: bulkActions.length,
      successCount,
      failureCount,
      results,
      partialSuccess: successCount > 0 && failureCount > 0,
      isCrossProgram,
      operationType
    };

    console.log(`🔄 ✅ Bulk actions complete (${operationType}):`, summary);
    return summary;
  }

  // Universal Bulk Actions Validation
  async validateBulkActionsAction(action, context) {
    const { data } = action;

    console.log('🔍 Validating bulk actions:', {
      hasData: !!data,
      hasActions: !!(data?.actions),
      actionsIsArray: Array.isArray(data?.actions),
      actionsLength: data?.actions?.length || 0,
      hasProgramIndex: data?.programIndex !== undefined,
      hasStage: !!data?.stage,
      data: data
    });

    if (!data) {
      console.warn('❌ Bulk actions validation failed: No data');
      return false;
    }

    if (!data.actions) {
      console.warn('❌ Bulk actions validation failed: No actions array');
      return false;
    }

    if (!Array.isArray(data.actions)) {
      console.warn('❌ Bulk actions validation failed: Actions is not an array');
      return false;
    }

    if (data.actions.length === 0) {
      console.warn('❌ Bulk actions validation failed: Empty actions array');
      return false;
    }

    // Validate each action has minimum required fields
    const invalidActions = data.actions.filter((action, index) => {
      const hasType = !!(action.type || action.capability || action.action);
      const hasData = !!(action.data || action.programIndex !== undefined);

      if (!hasType || !hasData) {
        console.warn(`❌ Invalid action at index ${index}:`, {
          hasType,
          hasData,
          action
        });
        return true;
      }
      return false;
    });

    if (invalidActions.length > 0) {
      console.warn(`❌ Bulk actions validation failed: ${invalidActions.length} invalid actions`);
      return false;
    }

    // Validate that we have at least programIndex and stage at the bulk level or action level
    const hasValidContext = data.programIndex !== undefined || 
                           data.actions.some(action => 
                             (action.data?.programIndex !== undefined || action.programIndex !== undefined) &&
                             (action.data?.stage || action.stage)
                           );

    if (!hasValidContext) {
      console.warn('❌ Bulk actions validation failed: No valid program/stage context');
      return false;
    }

    console.log('✅ Bulk actions validation passed');
    return true;
  }

  // Universal Bulk Actions Preview - ENHANCED APPROACH
  async previewBulkActionsAction(action, context) {
    const { data } = action;
    const { finalProgramSequence } = context;

    // Safety check for data structure
    if (!data || !data.actions || !Array.isArray(data.actions)) {
      console.warn('🔍 Invalid bulk actions data for preview:', data);
      return {
        type: 'bulk_actions',
        actionCount: 0,
        isCrossProgram: false,
        isCrossStage: false,
        isCrossView: false,
        actions: [],
        summary: 'Invalid bulk actions data',
        error: 'Missing or invalid actions array'
      };
    }

    // Create detailed preview for each action with error handling
    const actionDetails = data.actions.map((subAction, index) => {
      try {
        const actionProgramIndex = subAction.data?.programIndex;
        const actionStage = subAction.data?.stage;

        let programName = 'Unknown Program';
        if (finalProgramSequence && actionProgramIndex !== undefined && actionProgramIndex < finalProgramSequence.length) {
          const program = finalProgramSequence[actionProgramIndex];
          if (program) {
            programName = program.program_of_work || program.name || 'Unknown Program';
          }
        }

        // Generate specific preview based on action type
        let actionPreview = {};
        const actionType = subAction.type || 'unknown';

        switch (actionType) {
          case 'tool_duration_editing':
            actionPreview = {
              type: 'Duration Change',
              tool: subAction.data?.tool || subAction.data?.toolName || 'Unknown Tool',
              newDuration: subAction.data?.newDuration || subAction.data?.duration || 'Unknown',
              program: programName,
              stage: actionStage || 'Unknown Stage'
            };
            break;
          case 'add_tools_to_stages':
            actionPreview = {
              type: 'Add Tools',
              tools: subAction.data?.tools?.map(t => t.tool || t.name || t) || [],
              program: programName,
              stage: actionStage || 'Unknown Stage'
            };
            break;
          case 'remove_tools_from_stages':
            actionPreview = {
              type: 'Remove Tools',
              tools: subAction.data?.tools || [],
              program: programName,
              stage: actionStage || 'Unknown Stage'
            };
            break;
          case 'modify_tools_content':
            actionPreview = {
              type: 'Modify Tool',
              tool: subAction.data?.toolIdentifier || 'Unknown Tool',
              modifications: Object.keys(subAction.data?.modifications || {}).join(', ') || 'Unknown modifications',
              program: programName,
              stage: actionStage || 'Unknown Stage'
            };
            break;
          default:
            actionPreview = {
              type: actionType.replace(/_/g, ' '),
              program: programName,
              stage: actionStage || 'Unknown Stage',
              data: subAction.data || {}
            };
        }

        return actionPreview;
      } catch (error) {
        console.warn(`🔍 Error generating preview for action ${index}:`, error);
        return {
          type: 'Error',
          program: 'Unknown',
          stage: 'Unknown',
          error: error.message
        };
      }
    });

    const validActions = data.actions.filter(a => a && a.type);
    const actionTypes = validActions.map(a => a.type).join(', ');

    // Detect cross-view operations (actions affecting different UI views) - SCALABLE APPROACH
    const viewSet = new Set();
    validActions.forEach(action => {
      // Strategy component updates affect Design Strategy view
      if (action.type && (action.type.startsWith('update_') || 
                         action.type === 'update_strategy_component')) {
        viewSet.add('Design Strategy');
      }
      // Tool operations affect Step-by-Step view
      else if (action.type === 'tool_duration_editing' || 
               action.type === 'add_tools_to_stages' || 
               action.type === 'remove_tools_from_stages') {
        viewSet.add('Step-by-Step View');
      }
      // Program operations affect Program Management view
      else if (action.type === 'add_program' || 
               action.type === 'remove_program' ||
               action.type === 'reorder_programs') {
        viewSet.add('Program Management');
      }
      // Calendar operations affect Calendar view
      else if (action.type === 'schedule_update' || 
               action.type === 'timeline_adjustment') {
        viewSet.add('Calendar View');
      }
      // Plan operations affect Plan Overview
      else if (action.type === 'plan_save' || 
               action.type === 'plan_export' ||
               action.type === 'plan_duplicate') {
        viewSet.add('Plan Overview');
      }
    });

    const isCrossView = viewSet.size > 1;

    // Build summary with proper badge descriptions
    let summaryText = `${data.actions.length} actions`;
    if (data.isCrossProgram) {
      summaryText += ' across multiple programs';
    } else if (data.isCrossStage) {
      summaryText += ' across multiple stages';
    }
    if (isCrossView) {
      summaryText += ' across multiple views';
    }
    summaryText += `: ${actionTypes || 'Unknown types'}`;

    return {
      type: 'bulk_actions',
      actionCount: data.actions.length,
      isCrossProgram: data.isCrossProgram || false,
      isCrossStage: data.isCrossStage || false,
      isCrossView: isCrossView,
      actions: actionDetails,
      summary: summaryText
    };
  }

  // Tool Duration Parsing
  async parseToolDurationAction(aiResponse, context) {
    const actions = [];

    // Check if response contains duration-related keywords to prioritize this parser
    const isDurationRelated = /duration|hours|time|change|update|modify|adjust|extend|reduce/i.test(aiResponse);

    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];
    for (const match of jsonMatches) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        const parsed = JSON.parse(jsonStr);

        // If this looks like duration editing but was misclassified as add_tools_to_stages
        if (isDurationRelated && parsed.capability === 'add_tools_to_stages' && parsed.data?.tools) {
          // Convert to tool_duration_editing actions
          parsed.data.tools.forEach(tool => {
            if (tool.duration || tool.newDuration) {
              actions.push({
                data: {
                  toolName: tool.tool || tool.name,
                  newDuration: tool.duration || tool.newDuration,
                  programIndex: parsed.data.programIndex,
                  stage: parsed.data.stage,
                  currentDuration: tool.currentDuration
                },
                description: `Update ${tool.tool || tool.name} duration to ${tool.duration || tool.newDuration} hours`
              });
            }
          });
          return actions; // Return early to prevent double processing
        }

        if (parsed.capability === 'tool_duration_editing') {
          // Check if this is a bulk update with multiple tools - REDIRECT TO BULK_ACTIONS
          if (parsed.data?.tools && Array.isArray(parsed.data.tools)) {
            // Don't create individual actions, let bulk_actions handle this
            return [];
          } else {
            // Handle single tool duration update - support both new and legacy field names
            let normalizedData = {
              toolName: parsed.data?.toolName || parsed.data?.tool || parsed.data?.tool_name,
              newDuration: parsed.data?.newDuration || parsed.data?.duration || parsed.data?.new_duration,
              programIndex: parsed.data?.programIndex !== undefined ? parsed.data.programIndex : (parsed.data?.program_index !== undefined ? parsed.data.program_index : parsed.data?.program),
              stage: parsed.data?.stage || parsed.data?.stage_name,
              currentDuration: parsed.data?.currentDuration
            };

            console.log('🔍 Normalized tool duration data:', normalizedData);

            // Add the action to the actions array for this parser
            actions.push({
              data: normalizedData,
              description: `Update ${normalizedData.toolName} duration to ${normalizedData.newDuration} hours`,
              autoExecute: parsed.auto_execute === true,
            });
          }
        }
      } catch (e) {
        continue;
      }
    }

    return actions;
  }

  // Tool Duration Execution
  async executeToolDurationAction(action, context) {
    const { handleComponentAction } = context;
    if (!handleComponentAction) {
      throw new Error('No component action handler available');
    }

    return await handleComponentAction(
      'tool_duration_editing',
      'AIActionManager',
      action.data
    );
  }

  // Tool Duration Validation
  async validateToolDurationAction(action, context) {
    const { data } = action;
    return data && data.toolName && data.newDuration && data.programIndex !== undefined && data.stage;
  }

  // Tool Duration Preview
  async previewToolDurationAction(action, context) {
    const { data } = action;
    const { finalProgramSequence } = context;

    let programName = 'Unknown Program';
    let currentDuration = 'Unknown';

    if (data.programIndex !== undefined && finalProgramSequence[data.programIndex]) {
      const program = finalProgramSequence[data.programIndex];
      programName = program.program_of_work || 'Unknown Program';
      
      // Find the current duration from the actual tool data
      if (program.toolsByStage?.[data.stage]?.tools) {
        const tool = program.toolsByStage[data.stage].tools.find(t => t.tool === data.toolName);
        if (tool && tool.duration !== undefined) {
          currentDuration = tool.duration;
        }
      }
    }

    return {
      type: 'duration_change',
      toolName: data.toolName,
      program: programName,
      stage: data.stage,
      currentDuration: currentDuration,
      newDuration: data.newDuration
    };
  }

  // Add Tools Parsing
  async parseAddToolsAction(aiResponse, context) {
    const actions = [];
    const seenActions = new Set(); // Track unique actions to prevent duplicates

    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];
    for (const match of jsonMatches) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        const parsed = JSON.parse(jsonStr);

        if (parsed.capability === 'add_tools_to_stages') {
          const toolsToAdd = parsed.data.tools || [];

          // Skip if this is really a duration-only edit — parseToolDurationAction already handles it
          // A pure duration edit has tools with duration/newDuration but no new tool content fields
          const isDurationOnlyEdit = toolsToAdd.length > 0 && toolsToAdd.every(t =>
            (t.duration !== undefined || t.newDuration !== undefined) &&
            !t.description && !t.objective && !t.deliverable
          );
          const isDurationRelated = /duration|hours|time|change|update|modify|adjust|extend|reduce/i.test(aiResponse);
          if (isDurationOnlyEdit && isDurationRelated) {
            console.log('🔍 parseAddToolsAction: skipping duration-only JSON (handled by parseToolDurationAction)');
            continue;
          }
          
          // Create unique key using full canonical representation (not just tool names)
          // This preserves legitimate variations with different durations/descriptions
          const uniqueKey = JSON.stringify({
            programIndex: parsed.data?.programIndex,
            stage: parsed.data?.stage,
            tools: [...toolsToAdd].sort((a, b) => (a.tool || a.name || '').localeCompare(b.tool || b.name || ''))
          });
          
          // Skip if we've already seen this exact action
          if (seenActions.has(uniqueKey)) {
            console.log('🔍 Skipping duplicate add_tools_to_stages action');
            continue;
          }
          seenActions.add(uniqueKey);

          // Check if this is a bulk addition (multiple tools to same location)
          if (toolsToAdd.length > 1) {
            console.log('🔍 Multiple tools detected for same location - converting to bulk action');
            // Convert to bulk action format
            const bulkAction = {
              capabilityId: "bulk_actions",
              capability: "bulk_actions",
              description: `Add ${toolsToAdd.length} tools to ${parsed.data.stage} stage`,
              data: {
                programIndex: parsed.data.programIndex,
                stage: parsed.data.stage,
                isCrossProgram: false,
                actions: [{
                  type: "add_tools_to_stages",
                  data: parsed.data
                }]
              }
            };
            actions.push(bulkAction);

            continue;
          }

          actions.push({
            data: parsed.data,
            description: parsed.description || 'Add tools to stage'
          });
        }
      } catch (e) {
        continue;
      }
    }

    return actions;
  }

  // Add Tools Execution
  async executeAddToolsAction(action, context) {
    const { handleComponentAction, finalProgramSequence } = context;
    
    // FIX #38: Handle bulk program merges (data.programs) vs single tool additions (data.tools)
    const isBulkMerge = action.data?.merge_with_existing && action.data?.programs;
    
    if (isBulkMerge) {
      console.log('🔧 executeAddToolsAction: Bulk merge mode - merging programs with existing plan');
      // For bulk merges, we're adding new program data, not modifying existing
      if (!handleComponentAction) {
        throw new Error('No component action handler available');
      }
      return await handleComponentAction(
        'add_tools_to_stages',
        'AIActionManager',
        action.data
      );
    }
    
    // Standard single tool addition - validate existing programs
    if (!finalProgramSequence || finalProgramSequence.length === 0) {
      throw new Error(
        '❌ Cannot add tools: Your plan has no programs yet. Create a program first before adding tools to it.'
      );
    }
    
    const programIndex = action.data?.programIndex ?? 0;
    if (programIndex >= finalProgramSequence.length) {
      throw new Error(
        `❌ Cannot add tools to program ${programIndex}: Program doesn't exist. Your plan only has ${finalProgramSequence.length} program(s).`
      );
    }
    
    if (!handleComponentAction) {
      throw new Error('No component action handler available');
    }

    return await handleComponentAction(
      'add_tools_to_stages',
      'AIActionManager',
      action.data
    );
  }

  // Add Tools Validation
  async validateAddToolsAction(action, context) {
    const { data } = action;
    // FIX #38: Support both data.tools (single tool additions) and data.programs (bulk merges)
    const hasTools = data && data.tools && Array.isArray(data.tools) && data.tools.length > 0;
    const hasPrograms = data && data.programs && Array.isArray(data.programs) && data.programs.length > 0;
    
    if (hasTools || hasPrograms) {
      console.log('🔧 validateAddToolsAction: Valid data structure', {
        hasTools,
        toolsCount: data.tools?.length || 0,
        hasPrograms,
        programsCount: data.programs?.length || 0
      });
      return true;
    }
    
    console.log('🔧 validateAddToolsAction: Invalid data - missing tools or programs array');
    return false;
  }

  // Add Tools Preview
  async previewAddToolsAction(action, context) {
    const { data } = action;
    const { finalProgramSequence } = context;

    let programName = 'Unknown Program';
    if (data.programIndex !== undefined && finalProgramSequence[data.programIndex]) {
      programName = finalProgramSequence[data.programIndex].program_of_work;
    }

    const toolsToAdd = data.tools || [];
    const toolNames = toolsToAdd.map(tool => tool.tool || tool.name || 'Unknown Tool');

    // For single tool, use add_tool type; for multiple, should be bulk_actions
    if (toolsToAdd.length > 1) {
      return {
        type: 'bulk_actions',
        actionCount: toolsToAdd.length,
        isCrossProgram: false,
        isCrossView: false,
        actions: toolsToAdd.map(tool => ({
          type: 'Add Tool',
          tool: tool.tool || tool.name || 'Unknown Tool',
          program: programName,
          stage: data.stage || 'Unknown Stage'
        })),
        summary: `Add ${toolsToAdd.length} tools to ${data.stage || 'Unknown Stage'} stage`
      };
    }

    return {
      type: 'add_tool',
      toolName: toolNames.length === 1 ? toolNames[0] : `${toolNames.length} tools`,
      tool: toolNames[0], // Add this for compatibility
      tools: toolNames,
      program: programName,
      stage: data.stage || 'Unknown Stage',
      duration: toolsToAdd.length === 1 ? toolsToAdd[0].duration : 
                toolsToAdd.reduce((sum, tool) => sum + (tool.duration || 0), 0),
      toolCount: toolsToAdd.length
    };
  }

  // Remove Tools Parsing
  async parseRemoveToolsAction(aiResponse, context) {
    const actions = [];

    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];
    for (const match of jsonMatches) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        const parsed = JSON.parse(jsonStr);

        // Use standardized capability format only
        if (parsed.capability === 'remove_tools_from_stages') {
          // Analyze action scope for preview
          const scopeAnalysis = this.analyzeActionScope(parsed.data, context);

          actions.push({
            data: parsed.data,
            description: parsed.description || 'Remove tools from stage',
            scopeAnalysis,
            requiresPreview: true
          });
        }
      } catch (e) {
        continue;
      }
    }

    return actions;
  }

  // Analyze action scope for preview generation
  analyzeActionScope(actionData, context) {
    const result = {
      scope: 'single',
      affectedCount: 1,
      locations: [],
      requiresSelection: false
    };

    if (!actionData || !actionData.tools) {
      return result;
    }

    const toolName = Array.isArray(actionData.tools) ? actionData.tools[0] : actionData.tools;
    const toolNameToCheck = typeof toolName === 'string' ? toolName : (toolName.tool || toolName.name);

    // Check if the same tool exists in multiple programs/stages
    if (context.finalProgramSequence && actionData.tools) {
      let foundCount = 0;
      const locations = [];

      context.finalProgramSequence.forEach((program, programIndex) => {
        if (program.toolsByStage) {
          Object.entries(program.toolsByStage).forEach(([stageName, stageData]) => {
            if (stageData.tools) {
              const matchingTools = stageData.tools.filter(tool => 
                (tool.tool || tool.name) === toolNameToCheck
              );
              if (matchingTools.length > 0) {
                foundCount += matchingTools.length;
                locations.push({
                  program: program.program_of_work || program.name,
                  stage: stageName,
                  count: matchingTools.length,
                  programIndex,
                  tools: matchingTools
                });
              }
            }
          });
        }
      });

      if (foundCount > 1) {
        result.scope = 'multiple';
        result.affectedCount = foundCount;
        result.locations = locations;
        result.requiresSelection = true;
      }
    }

    return result;
  }

  // Remove Tools Execution
  async executeRemoveToolsAction(action, context) {
    const { handleComponentAction } = context;
    if (!handleComponentAction) {
      throw new Error('No component action handler available');
    }

    return await handleComponentAction(
      'remove_tools_from_stages',
      'AIActionManager',
      action.data
    );
  }

  // Remove Tools Validation
  async validateRemoveToolsAction(action, context) {
    const { data } = action;

    // Validate basic structure
    if (!data || data.programIndex === undefined || !data.stage) {
      return false;
    }

    // Normalize data structure: convert toolName to tools array BEFORE validation
    if (data.toolName && !data.tools) {
      data.tools = [data.toolName];
    }
    if (data.tool && !data.tools) {
      data.tools = [data.tool];
    }

    // Validate tools array exists and has content
    if (!data.tools || !Array.isArray(data.tools) || data.tools.length === 0) {
      return false;
    }

    // Validate each tool has a name or tool property
    return data.tools.every(tool => {
      if (typeof tool === 'string') return true;
      if (typeof tool === 'object' && (tool.tool || tool.name)) return true;
      return false;
    });
  }

  // Remove Tools Preview
  async previewRemoveToolsAction(action, context) {
    const { data } = action;
    const { finalProgramSequence } = context;

    let programName = 'Unknown Program';
    if (data.programIndex !== undefined && finalProgramSequence[data.programIndex]) {
      programName = finalProgramSequence[data.programIndex].program_of_work || 'Unknown Program';
    }

    // Handle both tools array and single toolName
    let toolsToRemove = [];
    if (data.tools && Array.isArray(data.tools)) {
      toolsToRemove = data.tools.map(tool => 
        typeof tool === 'string' ? tool : (tool.tool || tool.name || 'Unknown Tool')
      );
    } else if (data.toolName) {
      toolsToRemove = [data.toolName];
    } else if (data.tool) {
      toolsToRemove = [data.tool];
    }

    return {
      type: 'tool_removal',
      component: 'tool_management',
      program: programName,
      stage: data.stage,
      toolsToRemove,
      toolCount: toolsToRemove.length
    };
  }

  // ========================================
  // SAVE PLAN CAPABILITY (FIX #15)
  // ========================================

  // Save Plan Parsing - extract from JSON blocks like other capabilities
  async parseSavePlanAction(aiResponse, context) {
    const actions = [];

    // Look for JSON code blocks first (like other capabilities)
    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];
    for (const match of jsonMatches) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        const parsed = JSON.parse(jsonStr);

        if (parsed.capability === 'save_plan' && parsed.data) {
          console.log('🎯 save_plan JSON detected:', {
            hasEnabledFeatures: !!parsed.data.enabled_features,
            enabledFeatures: parsed.data.enabled_features,
            programCount: parsed.data.final_program_sequence?.length || 0,
          });

          actions.push({
            data: parsed.data,
            description: parsed.description || 'Enable features and create programs'
          });
        }
      } catch (e) {
        console.warn('Failed to parse save_plan JSON:', e.message);
        continue;
      }
    }

    // Also check for inline JSON (not in code blocks) for flexibility
    if (actions.length === 0) {
      const inlineRegex = /\{[\s\S]*?"capability":\s*"save_plan"[\s\S]*?\}/;
      const inlineMatch = aiResponse.match(inlineRegex);
      if (inlineMatch) {
        try {
          const parsed = JSON.parse(inlineMatch[0]);
          if (parsed.data) {
            console.log('🎯 save_plan inline JSON detected');
            actions.push({
              data: parsed.data,
              description: 'Enable features and create programs'
            });
          }
        } catch (e) {
          console.warn('Failed to parse inline save_plan JSON');
        }
      }
    }

    return actions;
  }

  // Save Plan Execution
  async executeSavePlanAction(action, context) {
    const { handleComponentAction } = context;
    if (!handleComponentAction) {
      throw new Error('No component action handler available');
    }

    console.log('🚀 Executing save_plan:', {
      enabledFeatures: action.data?.enabled_features,
      programCount: action.data?.final_program_sequence?.length || 0,
    });

    return await handleComponentAction(
      'save_plan',
      'AIActionManager',
      action.data
    );
  }

  // Save Plan Validation
  async validateSavePlanAction(action, context) {
    const { data } = action;
    // Must have enabled_features and program data
    return data && 
           data.enabled_features && 
           Array.isArray(data.enabled_features) &&
           data.enabled_features.length > 0 &&
           data.final_program_sequence &&
           Array.isArray(data.final_program_sequence);
  }

  // Save Plan Preview
  async previewSavePlanAction(action, context) {
    const { data } = action;
    const programs = data.final_program_sequence || [];
    const totalTools = programs.reduce((sum, prog) => {
      return sum + Object.values(prog.toolsByStage || {})
        .reduce((stageSum, stage) => stageSum + (stage.tools?.length || 0), 0);
    }, 0);
    
    return {
      type: 'feature_enablement',
      featuresToEnable: data.enabled_features,
      programCount: programs.length,
      stageCount: programs.length > 0 ? (programs[0].Stages?.length || 0) : 0,
      toolCount: totalTools,
      programNames: programs.map(p => p.program_of_work),
      summary: `Enable ${data.enabled_features.join(', ')} and create ${programs.length} program(s) with ${totalTools} tools`
    };
  }

  // Specific Strategy Update Parsing - only returns actions for the exact capability requested
  async parseSpecificStrategyUpdate(aiResponse, context, targetCapability) {
    const actions = [];

    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];
    for (const match of jsonMatches) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        const parsed = JSON.parse(jsonStr);

        // CRITICAL FIX: Only parse and return actions that exactly match the target capability
        if (parsed.capability === targetCapability) {
          console.log(`🔍 ✅ EXACT MATCH: ${targetCapability} detected during specific parsing:`, {
            capability: parsed.capability,
            hasData: !!parsed.data,
            dataContent: parsed.data?.content?.substring(0, 50) + '...'
          });

          actions.push({
            data: parsed.data,
            capability: parsed.capability,
            description: parsed.description || `Update ${parsed.capability.replace('update_', '').replace('_', ' ')}`,
            autoExecute: parsed.auto_execute === true,
          });
        } else if (parsed.capability && parsed.capability.startsWith('update_')) {
          console.log(`🔍 ❌ SKIPPING: ${parsed.capability} does not match target ${targetCapability}`);
        }
      } catch (e) {
        continue;
      }
    }

    console.log(`🔍 Specific strategy parsing result for ${targetCapability}:`, {
      actionsFound: actions.length,
      capabilities: actions.map(a => a.capability)
    });

    return actions;
  }

  // Generic Strategy Update Parsing - for the generic update_strategy_component capability
  async parseStrategyUpdateAction(aiResponse, context) {
    const actions = [];

    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];
    for (const match of jsonMatches) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        const parsed = JSON.parse(jsonStr);

        // Only handle generic update_strategy_component capability
        if (parsed.capability === 'update_strategy_component') {
          console.log('🔍 Generic strategy component detected during parsing:', {
            capability: parsed.capability,
            hasData: !!parsed.data,
            dataContent: parsed.data?.content?.substring(0, 50) + '...'
          });

          actions.push({
            data: parsed.data,
            capability: parsed.capability,
            description: parsed.description || `Update strategy component`
          });
        }
      } catch (e) {
        continue;
      }
    }

    console.log('🔍 Generic strategy parsing result:', {
      actionsFound: actions.length,
      capabilities: actions.map(a => a.capability)
    });

    return actions;
  }

  // Strategy Update Execution
  async executeStrategyUpdateAction(action, context) {
    const { handleComponentAction, currentPlanId } = context;
    if (!handleComponentAction) {
      throw new Error('No component action handler available');
    }

    // CRITICAL FIX: Check both capabilityId and capability fields
    const capabilityKey = action.capabilityId || action.capability;

    console.log('🔍 Strategy Update Execution - Debug info:', {
      capabilityId: action.capabilityId,
      capability: action.capability,
      usedCapabilityKey: capabilityKey,
      actionData: action.data,
      actionKeys: Object.keys(action),
      planId: currentPlanId
    });

    // Route directly to the specific capability instead of going through generic handler
    if (capabilityKey && capabilityKey !== 'update_strategy_component') {
      console.log(`🔧 Direct routing to specific capability: ${capabilityKey}`);
      return await handleComponentAction(
        {
          type: capabilityKey,
          capability: capabilityKey,
          component: 'AIActionManager',
          data: action.data,
          context: { planId: currentPlanId }
        }
      );
    }

    // Fallback for generic update_strategy_component
    const capabilityToComponent = {
      'update_goal_statement': 'goal_statement',
      'update_problem_statement': 'problem_statement', 
      'update_target_statement': 'target_statement',
      'update_design_challenge': 'design_challenge',
      'update_objectives': 'objectives',
      'update_deliverables': 'deliverables',
      'update_metrics': 'metrics',
      'update_stakeholders': 'stakeholders',
      'update_summary': 'summary',
      'update_experience_design_principles': 'experience_design_principles'
    };

    const componentType = capabilityToComponent[capabilityKey] || action.data.componentType || 'goal_statement';

    console.log('🔍 Strategy Update Execution - Fallback routing:', {
      capabilityKey,
      mappedComponentType: componentType,
      dataComponentType: action.data.componentType
    });

    // Prepare update data based on capability type
    let updateData = { ...action.data, componentType: componentType };

    // Handle different data structures for different capabilities
    if (capabilityKey === 'update_metrics' && action.data.metrics) {
      // For metrics, convert array to formatted string
      updateData.newValue = Array.isArray(action.data.metrics) 
        ? action.data.metrics.map(metric => `• ${metric}`).join('\n')
        : action.data.metrics;
    } else if (capabilityKey === 'update_objectives' && action.data.objectives) {
      // For objectives, convert array to formatted string
      updateData.newValue = Array.isArray(action.data.objectives)
        ? action.data.objectives.map(obj => `• ${obj}`).join('\n')
        : action.data.objectives;
    } else if (capabilityKey === 'update_stakeholders' && action.data.stakeholders) {
      // For stakeholders, handle array or string
      updateData.newValue = Array.isArray(action.data.stakeholders)
        ? action.data.stakeholders.join('\n')
        : action.data.stakeholders;
    } else {
      // Generic field extraction for all capabilities
      const capabilityFieldMap = {
        'update_target_statement': ['target_statement', 'targetStatement'],
        'update_goal_statement': ['goal_statement', 'goalStatement'],
        'update_problem_statement': ['problem_statement', 'problemStatement'],
        'update_summary': ['summary', 'short_summary', 'shortSummary'],
        'update_deliverables': ['deliverables', 'deliverables_statement', 'deliverablesStatement'],
        'update_experience_design_principles': ['experience_design_principles', 'experienceDesignPrinciples'],
        'update_design_challenge': ['design_challenge', 'designChallenge']
      };

      const expectedFields = capabilityFieldMap[capabilityKey];
      if (expectedFields) {
        // Find the first matching field with a value
        for (const field of expectedFields) {
          if (action.data[field]) {
            updateData.newValue = action.data[field];
            break;
          }
        }
      }
      
      // Fallback to content/value/text if no specific field found
      if (!updateData.newValue) {
        updateData.newValue = action.data.content || action.data.value || action.data.text;
      }
    }

    return await handleComponentAction(
      {
        type: 'update_strategy_component',
        capability: 'update_strategy_component',
        component: 'AIActionManager',
        data: updateData,
        context: { planId: currentPlanId }
      }
    );
  }

  // Strategy Update Validation
  async validateStrategyUpdateAction(action, context) {
    const { data } = action;
    const capability = action.capability || action.capabilityId;

    console.log('🔍 Strategy validation - capability:', capability, 'data:', data);

    // Generic validation for update_strategy_component
    if (data.componentType && data.newValue) {
      return true;
    }

    // Specific validation for direct component updates
    if (data.content || data.value || data.text) {
      return true;
    }

    // Capability-specific validation for all brief/strategy fields
    const capabilityFieldMap = {
      'update_target_statement': ['target_statement', 'targetStatement'],
      'update_goal_statement': ['goal_statement', 'goalStatement'],
      'update_problem_statement': ['problem_statement', 'problemStatement'],
      'update_summary': ['summary', 'short_summary', 'shortSummary'],
      'update_deliverables': ['deliverables', 'deliverables_statement', 'deliverablesStatement'],
      'update_experience_design_principles': ['experience_design_principles', 'experienceDesignPrinciples'],
      'update_metrics': ['metrics'],
      'update_objectives': ['objectives'],
      'update_stakeholders': ['stakeholders'],
      'update_design_challenge': ['design_challenge', 'designChallenge']
    };

    const expectedFields = capabilityFieldMap[capability];
    if (expectedFields) {
      const hasValidField = expectedFields.some(field => {
        const value = data[field];
        // For arrays, check if non-empty
        if (Array.isArray(value)) return value.length > 0;
        // For other values, check if truthy
        return !!value;
      });
      if (hasValidField) return true;
    }

    // Validate that we have either a field or component identifier
    return data && (data.field || data.component || data.componentType);
  }

  // Strategy Update Preview
  async previewStrategyUpdateAction(action, context) {
    const { data } = action;

    console.log('🔍 Strategy preview action:', {
      capabilityId: action.capabilityId,
      capability: action.capability,
      dataKeys: Object.keys(data || {}),
      context: !!context
    });

    // CRITICAL FIX: Always map capability IDs to readable component names FIRST
    let componentName = 'Strategy Component'; // Clear fallback

    const capability = action.capability || action.capabilityId;
    if (capability) {
      const componentMap = {
        'update_goal_statement': 'Goal Statement',
        'update_problem_statement': 'Problem Statement', 
        'update_target_statement': 'Target Statement',
        'update_objectives': 'Key Objectives',
        'update_deliverables': 'Deliverables',
        'update_metrics': 'Success Metrics',
        'update_stakeholders': 'Stakeholders',
        'update_summary': 'Project Summary',
        'update_experience_design_principles': 'Experience Design Principles',
        'update_strategy_component': this.getComponentNameFromData(data)
      };
      componentName = componentMap[capability] || componentName;

      console.log('🔍 Strategy preview mapping:', {
        capability,
        mappedComponentName: componentName,
        allMappings: Object.keys(componentMap)
      });
    }

    // Get current value from context/plan state if available
    let currentValue = 'Not set';
    if (context && (context.planState || context.finalProgramSequence || context.shortSummary)) {
      // Try to get from direct context first
      const contextData = {
        goalStatement: context.goalStatement,
        problemStatement: context.problemStatement,
        targetStatement: context.targetStatement,
        objectivesStatement: context.objectivesStatement,
        deliverablesStatement: context.deliverablesStatement,
        metricsStatement: context.metricsStatement,
        stakeholdersCustomersInvolvement: context.stakeholdersCustomersInvolvement,
        shortSummary: context.shortSummary
      };

      // Then try planState if direct context doesn't have it
      const planStateData = context.planState || {};

      const stateMap = {
        'Goal Statement': contextData.goalStatement || planStateData.goalStatement,
        'Problem Statement': contextData.problemStatement || planStateData.problemStatement,
        'Target Statement': contextData.targetStatement || planStateData.targetStatement,
        'Key Objectives': contextData.objectivesStatement || planStateData.objectivesStatement,
        'Deliverables': contextData.deliverablesStatement || planStateData.deliverablesStatement,
        'Success Metrics': contextData.metricsStatement || planStateData.metricsStatement,
        'Stakeholders': contextData.stakeholdersCustomersInvolvement || planStateData.stakeholdersCustomersInvolvement,
        'Project Summary': contextData.shortSummary || planStateData.shortSummary
      };

      currentValue = stateMap[componentName];

      // Handle array values for objectives
      if (componentName === 'Key Objectives' && Array.isArray(currentValue)) {
        currentValue = currentValue.filter(obj => obj && obj.length > 0).join('\n');
      }

      currentValue = currentValue || 'Not set';

      console.log('🔍 Current value lookup:', {
        componentName,
        currentValue: currentValue.substring(0, 50) + '...',
        hasContext: !!context,
        hasPlanState: !!context.planState
      });
    }

    // Determine new value from various possible fields
    // Use the same field extraction logic as the executor
    let newValue = data.newValue || data.content || data.value || data.text;
    
    // If no standard field found, check capability-specific fields
    if (!newValue) {
      const capabilityFieldMap = {
        'update_target_statement': ['target_statement', 'targetStatement'],
        'update_goal_statement': ['goal_statement', 'goalStatement'],
        'update_problem_statement': ['problem_statement', 'problemStatement'],
        'update_summary': ['summary', 'short_summary', 'shortSummary'],
        'update_deliverables': ['deliverables', 'deliverables_statement', 'deliverablesStatement'],
        'update_experience_design_principles': ['experience_design_principles', 'experienceDesignPrinciples'],
        'update_metrics': ['metrics'],
        'update_objectives': ['objectives'],
        'update_stakeholders': ['stakeholders'],
        'update_design_challenge': ['design_challenge', 'designChallenge']
      };

      const expectedFields = capabilityFieldMap[capability];
      if (expectedFields) {
        for (const field of expectedFields) {
          if (data[field]) {
            newValue = Array.isArray(data[field]) 
              ? data[field].join('\n') 
              : data[field];
            break;
          }
        }
      }
    }

    console.log('🔍 Strategy preview result:', {
      component: componentName,
      hasCurrentValue: currentValue !== 'Not set',
      hasNewValue: !!newValue,
      changeType: currentValue && currentValue !== 'Not set' ? 'replace' : 'add'
    });

    return {
      type: 'strategy_component_update',
      componentName: componentName,
      currentValue: currentValue !== 'Not set' ? currentValue.substring(0, 100) + '...' : 'Not set',
      newValue: newValue,
      view: 'Design Strategy',
      component: componentName,
      currentValue: currentValue,
      newValue: newValue,
      changeType: currentValue && currentValue !== 'Not set' ? 'replace' : 'add'
    };
  }

  // Helper method to determine component name from data when using generic capability
  getComponentNameFromData(data) {
    if (!data) return 'Strategy Component';

    const fallbackMap = {
      'goal_statement': 'Goal Statement',
      'goalstatement': 'Goal Statement',
      'goal': 'Goal Statement',
      'problem_statement': 'Problem Statement',
      'problemstatement': 'Problem Statement', 
      'problem': 'Problem Statement',
      'target_statement': 'Target Statement',
      'targetstatement': 'Target Statement',
      'target': 'Target Statement',
      'objectives': 'Key Objectives',
      'key_objectives': 'Key Objectives',
      'objectivesstatement': 'Key Objectives',
      'deliverables': 'Deliverables',
      'deliverablesstatement': 'Deliverables',
      'metrics': 'Success Metrics',
      'success_metrics': 'Success Metrics',
      'metricsstatement': 'Success Metrics',
      'stakeholders': 'Stakeholders',
      'stakeholderscustomersinvolvement': 'Stakeholders',
      'summary': 'Project Summary',
      'short_summary': 'Project Summary',
      'shortsummary': 'Project Summary'
    };

    const fieldName = (data.componentType || data.component || data.field || '').toLowerCase();
    return fallbackMap[fieldName] || 'Strategy Component';
  }

  async parseProductStrategyUpdate(aiResponse, context, targetCapability) {
    const actions = [];

    const jsonMatches = aiResponse.match(/```json\s*([\s\S]*?)\s*```/g) || [];
    for (const match of jsonMatches) {
      try {
        const jsonStr = match.replace(/```json\s*|\s*```/g, '');
        const parsed = JSON.parse(jsonStr);

        if (parsed.capability === targetCapability) {
          console.log(`🔍 Product strategy capability detected (code-fenced): ${targetCapability}`);
          actions.push({
            data: parsed.data || parsed,
            capability: parsed.capability,
            capabilityId: parsed.capability,
            description: parsed.description || `Update ${targetCapability.replace('update_', '').replace(/_/g, ' ')}`,
            autoExecute: parsed.auto_execute === true,
          });
        }
      } catch (e) {
        continue;
      }
    }

    if (actions.length === 0) {
      const capabilityPattern = new RegExp(`\\{\\s*"capability"\\s*:\\s*"${targetCapability}"\\s*,\\s*"data"\\s*:`);
      const match = aiResponse.match(capabilityPattern);
      if (match) {
        const startIndex = aiResponse.indexOf(match[0]);
        let braceCount = 0;
        let endIndex = -1;
        for (let i = startIndex; i < aiResponse.length; i++) {
          if (aiResponse[i] === '{') braceCount++;
          if (aiResponse[i] === '}') braceCount--;
          if (braceCount === 0 && i > startIndex) {
            endIndex = i + 1;
            break;
          }
        }
        if (endIndex > startIndex) {
          try {
            const jsonStr = aiResponse.substring(startIndex, endIndex);
            const parsed = JSON.parse(jsonStr);
            if (parsed.capability === targetCapability) {
              console.log(`🔍 Product strategy capability detected (inline): ${targetCapability}`);
              actions.push({
                data: parsed.data || parsed,
                capability: parsed.capability,
                capabilityId: parsed.capability,
                description: parsed.description || `Update ${targetCapability.replace('update_', '').replace(/_/g, ' ')}`,
                autoExecute: parsed.auto_execute === true,
              });
            }
          } catch (e) {
            console.warn(`⚠️ Failed to parse inline capability JSON for ${targetCapability}:`, e.message);
          }
        }
      }
    }

    return actions;
  }

  async executeProductStrategyUpdateAction(action, context) {
    const { handleComponentAction, currentPlanId } = context;

    const capabilityKey = action.capabilityId || action.capability;
    console.log(`🔧 Product Strategy Executor: Executing ${capabilityKey}`, action.data);
    console.log(`🔧 Product Strategy Executor: planId from context: ${currentPlanId}`);

    if (handleComponentAction) {
      return await handleComponentAction(
        {
          type: capabilityKey,
          capability: capabilityKey,
          component: 'AIActionManager',
          data: action.data,
          context: { planId: currentPlanId }
        }
      );
    }

    throw new Error(`No component action handler available for ${capabilityKey}`);
  }

  async validateProductStrategyUpdateAction(action, context) {
    const { data } = action;
    if (!data) return false;

    if (typeof data === 'object' && Object.keys(data).length > 0) {
      return true;
    }

    return !!(data.content || data.value || data.text);
  }

  async previewProductStrategyUpdateAction(action, context) {
    const capability = action.capability || action.capabilityId;
    const { data } = action;

    const nameMap = {
      'update_strategic_themes': 'Strategic Themes',
      'update_design_vision': 'Design Vision',
      'update_audience_insights': 'Audience Insights',
      'update_opportunity_areas': 'Opportunity Areas',
      'update_design_hypotheses': 'Design Hypotheses',
      'update_brief_deconstruction': 'Brief Deconstruction',
      'update_information_assessment': 'Information Assessment',
      'update_strategic_guidance': 'Strategic Guidance',
      'update_competitive_ux_positioning': 'Competitive Positioning',
      'update_experience_design_principles': 'Experience Design Principles',
      'update_success_metrics': 'Success Metrics',
      'update_unmet_needs': 'Unmet Needs',
    };

    const componentName = nameMap[capability] || capability.replace('update_', '').replace(/_/g, ' ');

    let newValue = data.content || data.value || data.text;
    if (!newValue) {
      const fieldMap = {
        'update_strategic_themes': ['strategicThemes', 'strategic_themes', 'themes'],
        'update_design_vision': ['designVision', 'design_vision'],
        'update_audience_insights': ['audienceInsights', 'audience_insights', 'audience'],
        'update_opportunity_areas': ['opportunityAreas', 'opportunity_areas', 'opportunities'],
        'update_design_hypotheses': ['designHypotheses', 'design_hypotheses', 'hypotheses'],
        'update_brief_deconstruction': ['briefDeconstruction', 'brief_deconstruction'],
        'update_information_assessment': ['informationAssessment', 'information_assessment'],
        'update_strategic_guidance': ['strategicGuidance', 'strategic_guidance'],
        'update_competitive_ux_positioning': ['competitiveUxPositioning', 'competitive_ux_positioning', 'competitivePositioning', 'positioning_statement'],
        'update_experience_design_principles': ['experienceDesignPrinciples', 'experience_design_principles', 'principles'],
        'update_success_metrics': ['successMetrics', 'success_metrics', 'metrics'],
        'update_unmet_needs': ['unmetNeeds', 'unmet_needs', 'needs'],
      };
      const fields = fieldMap[capability] || [];
      for (const field of fields) {
        if (data[field]) {
          newValue = Array.isArray(data[field]) 
            ? JSON.stringify(data[field], null, 2).substring(0, 200) + '...'
            : typeof data[field] === 'object'
            ? JSON.stringify(data[field], null, 2).substring(0, 200) + '...'
            : data[field];
          break;
        }
      }
      if (!newValue) {
        const { _itemIndex, ...rest } = data;
        const keys = Object.keys(rest);
        if (keys.length > 0) {
          const firstVal = rest[keys[0]];
          newValue = typeof firstVal === 'string' ? firstVal : JSON.stringify(firstVal, null, 2).substring(0, 200) + '...';
        }
      }
    }

    return {
      type: 'product_strategy_update',
      componentName,
      view: 'Product Strategy',
      component: componentName,
      newValue: newValue || 'Updated content',
      changeType: 'replace'
    };
  }

  // Clean message text by removing parsed actions
  cleanMessageText(text, detectedActions) {
    let cleanText = text;

    // Remove JSON code blocks — replace with a space to avoid word concatenation
    cleanText = cleanText.replace(/\s*```json\s*[\s\S]*?\s*```\s*/g, ' ');
    cleanText = cleanText.replace(/\s*```[\s\S]*?\s*```\s*/g, ' ');

    // Remove markdown artifacts and formatting remnants
    cleanText = cleanText.replace(/###\s*JSON:\s*copy/gi, '');
    cleanText = cleanText.replace(/###\s*JSON\s*$/gm, '');
    cleanText = cleanText.replace(/^\s*JSON:\s*copy\s*$/gm, '');

    // Remove technical references to JSON creation and actions - enhanced
    cleanText = cleanText.replace(/To proceed[^,]*,\s*I will create a JSON action[^.]*\./gi, '');
    cleanText = cleanText.replace(/I will create a JSON action[^.]*\./gi, '');
    cleanText = cleanText.replace(/Here's the JSON action[^:]*:/gi, '');
    cleanText = cleanText.replace(/Here is the JSON action data[^:]*:/gi, '');
    cleanText = cleanText.replace(/JSON Action Data[^:]*:/gi, '');
    cleanText = cleanText.replace(/\s*JSON Action Data[^:]*:\s*/gi, '');
    cleanText = cleanText.replace(/I'll prepare the JSON action data[^.]*\./gi, '');
    cleanText = cleanText.replace(/Let me generate the JSON action[^.]*\./gi, '');
    cleanText = cleanText.replace(/Creating the JSON action[^.]*\./gi, '');
    cleanText = cleanText.replace(/Generating action data[^.]*\./gi, '');
    cleanText = cleanText.replace(/Here is the JSON action data for the changes[^:]*:/gi, '');
    cleanText = cleanText.replace(/Here is the JSON action data for the UI preview generation[^:]*:/gi, '');

    // Remove AI agent capability and action references — replace with space to prevent word concatenation
    cleanText = cleanText.replace(/\{[\s\S]*?"capability":\s*"save_plan"[\s\S]*?\}\s*/gi, ' ');
    cleanText = cleanText.replace(/\{[\s\S]*?"capability":\s*"add_tools_to_stages"[\s\S]*?\}\s*/gi, ' ');
    cleanText = cleanText.replace(/\{[\s\S]*?"capability":\s*"[^"]*"[\s\S]*?\}\s*/gi, ' ');
    cleanText = cleanText.replace(/\s*\{[\s\S]*?"action":\s*"component_capability"[\s\S]*?\}\s*/gi, ' ');
    cleanText = cleanText.replace(/\s*\{[\s\S]*?"action":\s*"[^"]*"[\s\S]*?\}\s*/gi, ' ');
    cleanText = cleanText.replace(/\{[\s\S]*?"(capability|data|enabled_features|final_program_sequence)"[\s\S]*?\}\s*/gi, ' ');

    // Remove technical preview explanations - enhanced
    cleanText = cleanText.replace(/I will now generate a preview of these changes\.\s*/gi, '');
    cleanText = cleanText.replace(/Please confirm by clicking[^!]*!/gi, '');
    cleanText = cleanText.replace(/Once you review and confirm[^.]*\./gi, '');
    cleanText = cleanText.replace(/You can review the changes[^.]*\./gi, '');
    cleanText = cleanText.replace(/Click.*?Apply.*?to execute[^.]*\./gi, '');
    cleanText = cleanText.replace(/The preview will show[^.]*\./gi, '');
    cleanText = cleanText.replace(/The system will now[^.]*\./gi, '');
    cleanText = cleanText.replace(/This will trigger[^.]*\./gi, '');
    cleanText = cleanText.replace(/Please confirm by clicking "Apply Change" in the preview once it's displayed!/gi, '');

    // Remove action detection patterns
    cleanText = cleanText.replace(/I will (perform|execute|apply) the following actions?:?\s*/gi, '');
    cleanText = cleanText.replace(/Here are the changes I will make:?\s*/gi, '');
    cleanText = cleanText.replace(/I'll make these updates:?\s*/gi, '');

    // Remove any remaining raw JSON blocks that were parsed
    detectedActions.forEach(action => {
      if (action.originalResponse) {
        const jsonStr = JSON.stringify(action.data);
        cleanText = cleanText.replace(new RegExp(jsonStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), ' ');
      }
    });

    // Clean up references to technical implementation
    cleanText = cleanText.replace(/\s*Here's what will happen:\s*/gi, '\n\n**Recommended changes:**\n\n');
    cleanText = cleanText.replace(/\s*Preview:\s*/gi, '');
    cleanText = cleanText.replace(/\s*Action data:\s*/gi, '');

    // Remove standalone JSON objects and fragments — replace with space to prevent word concatenation
    cleanText = cleanText.replace(/\{\s*"[^"]*":\s*[^}]*\}/gi, ' ');
    cleanText = cleanText.replace(/^\s*[\{\}]\s*$/gm, '');

    // Clean up extra whitespace — collapse multiple spaces but preserve newlines for markdown structure
    cleanText = cleanText.replace(/[^\S\n]+/g, ' ').trim();
    cleanText = cleanText.replace(/^\s*\}\s*$/gm, '');
    cleanText = cleanText.replace(/\n{3,}/g, '\n\n');

    // Remove any trailing technical references
    cleanText = cleanText.replace(/\s*$/, '');

    // Remove lines that are just "Let's apply these changes!" or similar
    cleanText = cleanText.replace(/^Let's apply these changes!\s*$/gm, '');
    cleanText = cleanText.replace(/^Please confirm!\s*$/gm, '');

    // Remove empty lines and normalize spacing
    cleanText = cleanText.split('\n').filter(line => line.trim().length > 0).join('\n\n');

    // Final cleanup of any remaining markdown artifacts
    cleanText = cleanText.replace(/^\s*copy\s*$/gm, '');
    cleanText = cleanText.replace(/^\s*JSON\s*$/gm, '');

    return cleanText || 'Changes detected and ready to apply.';
  }
}

// Create and export singleton instance
const aiActionManager = new AIActionManager();
export default aiActionManager;